/**
 * Types for Cash Positioning UI, matching the Smithy data model in LiquidityEngineDataModel.
 */

export type PositioningRegion = 'APAC' | 'EMEA' | 'AMERICAS';
export type WireType = 'ZBA_SWEEP' | 'AQ_POSITIONING' | 'NO_WIRE';
export type WireApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type EntityProcessingStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export interface FlowBreakdownEntry {
    budgetCode: string;
    netFlow: number;
    count: number;
}

export interface BalanceBreakdownEntry {
    accountCode: string;
    currency: string;
    openingBalance: number;
    intradayNet: number;
    positioningResult: number;
    targetBalance: number;
    wireRecommendationAmount: number;
    contribution: number;
}

export interface WireRecommendation {
    entityId: string;
    processingDateWireType: string;
    proposalId?: string;
    sourceAccount?: string;
    destinationAccount?: string;
    amount?: number;
    currency?: string;
    wireDate?: string;
    direction?: string;
    agentGenerated?: boolean;
    wireType?: WireType;
    totalBalance?: number;
    targetBalance?: number;
    excessBalance?: number;
    accountsAggregated?: number;
    balanceBreakdown?: BalanceBreakdownEntry[];
    reasoning?: string;
    calculationLogic?: Record<string, string>;
    approvalStatus?: WireApprovalStatus;
    approvalId?: string;
    region?: PositioningRegion;
    runId?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface VarianceOutput {
    accountId: string;
    processingDate: string;
    currency?: string;
    ledgerOpening?: number;
    ledgerClosing?: number;
    bankTransactionTotal?: number;
    expectedClosing?: number;
    variance?: number;
    hasVariance?: boolean;
    transactionCount?: number;
    transactions?: AccountTransaction[];
    region?: PositioningRegion;
}

export interface AccountTransaction {
    source?: string;
    account?: string;
    flowAmount?: number;
    budgetCode?: string;
    flow?: string;
    flowStatus?: string;
    debit?: number;
    credit?: number;
    valueDate?: string;
    description?: string;
}

export interface LedgerBalance {
    accountId?: string;
    date?: string;
    currency?: string;
    openingBalance?: number;
    closingBalance?: number;
}

export interface AgentSummary {
    region?: PositioningRegion;
    processingDate?: string;
    runId?: string;
    status?: EntityProcessingStatus;
    totalEntities?: number;
    processedEntities?: number;
    failedEntities?: number;
    skippedEntities?: number;
    zbaWireCount?: number;
    aqTransferCount?: number;
    noWireCount?: number;
    varianceCount?: number;
    totalAmountsByCurrency?: Record<string, string>;
    startedAt?: string;
    completedAt?: string;
}

export interface EntityProcessingState {
    runId?: string;
    entityId?: string;
    region?: PositioningRegion;
    processingDate?: string;
    status?: EntityProcessingStatus;
    errorMessage?: string;
    startedAt?: string;
    completedAt?: string;
    entitiesInRun?: number;
}

export interface ProcessingStatusResponse {
    status?: EntityProcessingStatus;
    lastRunDate?: string;
    lastRunId?: string;
    entityStates?: EntityProcessingState[];
}

export interface QuerySource {
    sourceType?: string;
    sourceId?: string;
    relevance?: number;
}

export interface QueryResponse {
    answer?: string;
    confidence?: number;
    sources?: QuerySource[];
}

export const REGION_OPTIONS: { value: PositioningRegion; label: string }[] = [
    { value: 'APAC', label: 'APAC' },
    { value: 'EMEA', label: 'EMEA' },
    { value: 'AMERICAS', label: 'Americas' },
];
